/**
 * useTeamToday — the React binding for the Team Today awareness dashboard.
 *
 * Strict layering: the page calls THIS hook; the hook composes the feature
 * STORES (Goal / Task / Workstream / Blocker / Change), each of which talks to
 * the api facade. The page never imports a store or the api directly.
 *
 * The dashboard is a pure read AGGREGATION computed ENTIRELY CLIENT-SIDE over
 * the stored documents: it loads every relevant collection once (in parallel),
 * hydrates the full goal documents so it can read their milestones, folds the
 * open-blocker delivery impact via the {@link ../blockers/blockerImpact}
 * selector, and derives the summary tiles + upcoming milestones + the "who is
 * working on what?" list. It owns no persistence.
 *
 * Deliberately NO productivity/velocity/leaderboard metric — every figure is an
 * awareness signal, never a per-person score.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GoalStore } from '../goals/goalStore';
import { TaskStore } from '../tasks/taskStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { BlockerStore } from '../blockers/blockerStore';
import { ChangeStore } from '../changes/changeStore';
import { TeamDocumentStore } from '../documents/documentStore';
import { selectOverallBlockerImpact } from '../blockers/blockerImpact';
import { WORKSTREAM_STAGES } from '../workstreams/workstreamConfig';
import type { TeamTodaySnapshot, UpcomingMilestone, WhoIsWorkingGroup } from './types';
import type { Goal } from '../goals/types';

/** Task statuses considered "done" for the sprint-completion figure. */
const DONE_STATUS = 'DONE';
/** The workstream stage that means "no longer active". */
const COMPLETE_STAGE = 'Complete';
/** Window (days) for the "recent changes" figure. */
const RECENT_CHANGES_WINDOW_DAYS = 7;
/** Max upcoming milestones surfaced. */
const MILESTONE_LIMIT = 8;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Whole days from now until a yyyy-mm-dd date (negative when in the past). */
function daysUntil(date: string): number | undefined {
  const d = new Date(date.length === 10 ? `${date}T00:00:00` : date);
  if (Number.isNaN(d.getTime())) return undefined;
  return Math.round((d.getTime() - Date.now()) / MS_PER_DAY);
}

const EMPTY_SNAPSHOT: TeamTodaySnapshot = {
  summary: {
    goalsOnTrack: 0,
    goalsAtRisk: 0,
    goalsOffTrack: 0,
    totalGoals: 0,
    sprintCompletionPct: undefined,
    sprintDone: 0,
    sprintTotal: 0,
    activeWorkstreams: 0,
    openBlockers: 0,
    blockerForecastImpactDays: 0,
    recentChanges: 0,
    newDocuments: 0,
  },
  upcomingMilestones: [],
  whoIsWorking: [],
};

export interface UseTeamTodayResult {
  snapshot: TeamTodaySnapshot;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useTeamToday(): UseTeamTodayResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      goals: new GoalStore(api),
      tasks: new TaskStore(api),
      workstreams: new WorkstreamStore(api),
      blockers: new BlockerStore(api),
      changes: new ChangeStore(api),
      documents: new TeamDocumentStore(api),
    }),
    [api],
  );

  const [snapshot, setSnapshot] = useState<TeamTodaySnapshot>(EMPTY_SNAPSHOT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [goalRows, allTasks, wsRows, blockerRows, changeRows, docRows] =
        await Promise.all([
          stores.goals.listSummaries(),
          stores.tasks.listAll(),
          stores.workstreams.listSummaries(),
          stores.blockers.listSummaries(),
          stores.changes.listSummaries(),
          stores.documents.listSummaries(),
        ]);

      // Hydrate full goal docs so we can read their milestones (not on the
      // pointer-row summary). Missing/deleted goals are skipped.
      const fullGoals = (
        await Promise.all(goalRows.map((g) => stores.goals.get(g.id)))
      ).filter((g): g is Goal => !!g && !g.deleted);

      // --- Goal status tiles ---
      const goalsOnTrack = goalRows.filter((g) => g.status === 'ON_TRACK').length;
      const goalsAtRisk = goalRows.filter((g) => g.status === 'AT_RISK').length;
      const goalsOffTrack = goalRows.filter((g) => g.status === 'OFF_TRACK').length;

      // --- Sprint completion from tasks ---
      const sprintTotal = allTasks.length;
      const sprintDone = allTasks.filter((t) => t.status === DONE_STATUS).length;
      const sprintCompletionPct =
        sprintTotal === 0 ? undefined : Math.round((sprintDone / sprintTotal) * 100);

      // --- Active workstreams ---
      const activeWorkstreams = wsRows.filter(
        (w) => (w.currentStage || '') !== COMPLETE_STAGE,
      ).length;

      // --- Blockers + forecast impact (via selector) ---
      const overallImpact = selectOverallBlockerImpact(blockerRows);

      // --- Recent changes ---
      const since = Date.now() - RECENT_CHANGES_WINDOW_DAYS * MS_PER_DAY;
      const recentChanges = changeRows.filter((c) => {
        const t = new Date(c.createdAt).getTime();
        return Number.isFinite(t) && t >= since;
      }).length;

      // --- New documents (registered in the recency window) ---
      const newDocuments = docRows.filter((d) => {
        const t = new Date(d.createdAt).getTime();
        return Number.isFinite(t) && t >= since;
      }).length;

      // --- Upcoming milestones (from full goals) ---
      const goalTitleById = new Map(fullGoals.map((g) => [g.id, g.title]));
      const milestones: UpcomingMilestone[] = [];
      for (const g of fullGoals) {
        const ms = g.nextMilestone;
        if (!ms?.dueDate) continue;
        const d = daysUntil(ms.dueDate);
        if (d === undefined) continue;
        milestones.push({
          goalId: g.id,
          goalTitle: g.title,
          label: ms.label || 'Milestone',
          dueDate: ms.dueDate,
          daysUntil: d,
          overdue: d < 0 && g.status !== 'COMPLETED',
        });
      }
      milestones.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
      const upcomingMilestones = milestones.slice(0, MILESTONE_LIMIT);

      // --- Who is working on what (active workstreams grouped by engineer) ---
      const byEngineer = new Map<string, WhoIsWorkingGroup>();
      for (const w of wsRows) {
        if ((w.currentStage || '') === COMPLETE_STAGE) continue;
        const engineer = w.engineer || '—';
        const group = byEngineer.get(engineer) ?? { engineer, items: [] };
        group.items.push({
          workstreamId: w.id,
          title: w.title || 'Untitled workstream',
          stage: w.currentStage || '',
          goalId: w.goalId,
          goalTitle: w.goalId ? goalTitleById.get(w.goalId) : undefined,
        });
        byEngineer.set(engineer, group);
      }
      const stageRankOf = (s: string) => {
        const i = WORKSTREAM_STAGES.findIndex((o) => o.value === s);
        return i === -1 ? WORKSTREAM_STAGES.length : i;
      };
      const whoIsWorking = Array.from(byEngineer.values())
        .sort((a, b) => a.engineer.localeCompare(b.engineer))
        .map((group) => ({
          ...group,
          items: group.items
            .slice()
            .sort((a, b) => stageRankOf(a.stage) - stageRankOf(b.stage)),
        }));

      setSnapshot({
        summary: {
          goalsOnTrack,
          goalsAtRisk,
          goalsOffTrack,
          totalGoals: goalRows.length,
          sprintCompletionPct,
          sprintDone,
          sprintTotal,
          activeWorkstreams,
          openBlockers: overallImpact.openBlockerCount,
          blockerForecastImpactDays: overallImpact.totalForecastImpactDays,
          recentChanges,
          newDocuments,
        },
        upcomingMilestones,
        whoIsWorking,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Team Today.');
    } finally {
      setLoading(false);
    }
  }, [stores]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { snapshot, loading, error, refresh };
}
